import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTaskFlowParam } from './useTaskFlowParam';
import { buildVaActionItems, type VaWorkspaceFlow } from './manage-vas/vaActionItems';
import { TaskActionsBox } from './TaskActionsBox';
import { TaskColumnFill, TaskScrollColumn } from './TaskScrollColumn';
import { PatientExternalLinks } from './PatientExternalLinks';
import { VaFlowView, type VaFullViewFlow } from './manage-vas/VaFlowView';
import { DmControlRoute } from '../../patient-files/actions/dm/control/DmControlRoute';
import { useDmControlRoute } from '../../patient-files/actions/dm/control/useDmControlRoute';
import { ScanComplianceStrip } from '../../patient-files/overview/ScanComplianceStrip';
import { VaWorkPanel } from './manage-vas/VaWorkPanel';
import { useVaHandoff, type VaHandoffKind } from './manage-vas/useVaHandoff';
import { resolveRoundStart } from './manage-vas/vaRound';
import { VaHandoffDialog } from './manage-vas/VaHandoffDialog';
import { TaskSidebarPanel } from './TaskSidebarPanel';
import { buildSidebarTabs, DM_VA_TABS, IN_TREATMENT_TABS } from './taskSidebarTabs';
import { DM_VA_NEEDED_STATUS_ID } from '../constants';
import type { TaskBodyProps } from './index';
import type { OutgoingAction } from '../../../services/workQueue';
import type { OpenVirtualAppointmentsNowResult } from '../../../services/virtualAppointments';
import { OpenVirtualAppointmentsNowModal } from '../../virtual-appointments/OpenVirtualAppointmentsNowModal';
import { openNowFeedback } from '../../virtual-appointments/openNowOutcome';
import { useToast } from '../../../providers/ToastProvider';
import { usePatientOpenVirtualAppointments } from '../../virtual-appointments/usePatientOpenVirtualAppointments';
import { useVirtualAppointmentRound } from '../../../hooks/useVirtualAppointments';
import { usePatientCard } from '../../../hooks/usePatientCard';

const FULL_VIEW_FLOWS: readonly VaFullViewFlow[] = ['refinement', 'retention'];
// Floor for the tab bar, folded handoff thread and VA list; the column scrolls past it. The first
// card opens expanded, so about two VA rows show fully at 1470x830.
const VA_LIST_MIN_HEIGHT = 'min-h-[320px]';

// Tx "DM/Clinical VA needed": work the open VAs on the left, patient context on the right, and
// leave the step (trigger, hand back, continue) from the actions card under the list.
export function ManageVirtualAppointmentsTask({
    workCase: c,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    // Both live in the URL, so browser Back walks out of them: monitoring is a modal over the
    // workspace, the triggers take the whole view.
    const { flow, open: showFlow, close: closeFlow } = useTaskFlowParam(FULL_VIEW_FLOWS);
    const dmControl = useDmControlRoute(c.id);
    const openFlow = (next: VaWorkspaceFlow) =>
        next === 'manage-monitoring' ? dmControl.open() : showFlow(next);
    const [handoff, setHandoff] = useState<VaHandoffKind | null>(null);
    const [openingVas, setOpeningVas] = useState(false);
    const { showToast } = useToast();
    // Already in a VA step, the new VAs just join the open or planned list (refreshed by the hook).
    const handleVasOpened = useCallback(
        (result: OpenVirtualAppointmentsNowResult) => {
            setOpeningVas(false);
            const feedback = openNowFeedback(result);
            if (feedback) showToast(feedback.message, feedback.tone);
        },
        [showToast]
    );
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
    }, [onPayloadChange]);
    const round = useVirtualAppointmentRound(c.id);
    const roundStartedAt = resolveRoundStart(round.startedAt, c.stepEnteredAt);
    const { data: card } = usePatientCard(c.id);
    const dmId = card?.monitoring?.dmId ?? null;
    const currentAligner = card?.monitoring?.currentAligner ?? c.currentAligner ?? null;
    const totalAligners = card?.monitoring?.totalAligners ?? c.totalAligners ?? null;
    const treatmentStartDate = card?.monitoring?.treatmentStartDate ?? null;
    const vas = usePatientOpenVirtualAppointments(c.id, roundStartedAt, { treatmentStartDate });
    const handoffRunner = useVaHandoff(c.id, onTaskComplete);
    // Every way out of the step lands on the completion page for the move it made.
    const handleTriggered = useCallback(
        (completed: OutgoingAction) => onTaskComplete?.(completed),
        [onTaskComplete]
    );
    const tabs = useMemo(
        () =>
            buildSidebarTabs(
                c.id,
                c.statusId === DM_VA_NEEDED_STATUS_ID ? DM_VA_TABS : IN_TREATMENT_TABS,
                null,
                { manageMonitoring: true }
            ),
        [c.id, c.statusId]
    );
    const actionItems = buildVaActionItems({
        statusId: c.statusId,
        loadStatus: vas.status,
        open: vas.open,
        isDmLinked: dmId != null,
        onOpenFlow: openFlow,
        onHandoff: setHandoff,
        onOpenNow: () => setOpeningVas(true)
    });
    const closeHandoff = () => {
        setHandoff(null);
        handoffRunner.resetError();
    };

    if (flow !== null) {
        return (
            <VaFlowView
                flow={flow}
                patientId={c.id}
                patientName={c.name}
                onBack={closeFlow}
                onSubmitted={handleTriggered}
            />
        );
    }
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex gap-3 px-4 pt-3 pb-3">
                <TaskScrollColumn gapClassName="gap-3">
                    <ScanComplianceStrip patientId={c.id} dmId={dmId} />
                    <TaskColumnFill minHeightClassName={VA_LIST_MIN_HEIGHT}>
                        <VaWorkPanel
                            patientId={c.id}
                            vas={vas}
                            currentAligner={currentAligner}
                            totalAligners={totalAligners}
                            treatmentStartDate={treatmentStartDate}
                            dmLinked={dmId != null}
                            handoffs={round.handoffs}
                            onRetryRound={round.failed ? round.retry : undefined}
                        />
                    </TaskColumnFill>
                    <TaskActionsBox
                        items={actionItems}
                        headerRight={<PatientExternalLinks patientId={c.id} />}
                    />
                </TaskScrollColumn>
                <div className="flex-[7] min-w-0 min-h-0">
                    <TaskSidebarPanel tabs={tabs} keepMounted />
                </div>
            </div>
            <DmControlRoute patientId={c.id} patientName={c.name} />
            {openingVas && (
                <OpenVirtualAppointmentsNowModal
                    patientId={c.id}
                    allowMoveOnly={false}
                    canOpenNow
                    onClose={() => setOpeningVas(false)}
                    onOpened={handleVasOpened}
                />
            )}
            {handoff !== null && (
                <VaHandoffDialog
                    kind={handoff}
                    runner={handoffRunner}
                    open={vas.open}
                    onClose={closeHandoff}
                />
            )}
        </div>
    );
}
